import { useRecoilState } from "recoil";
import { subjectImages, useRefreshInterIdSection, useRefreshSubjectIfExpired } from "../state";
import { useSessionApiClient } from "../api/sessions";
import {
  CLEAN_IMAGE_STATE,
  DEFAULT_STATE_TRANSITIONS,
  MERGE_IMAGE_STATE,
  REMOVE_NEAR_DUPLICATE_IMAGE_STATE,
  REMOVE_NOISY_IMAGE_STATE,
  UNCLEAN_IMAGE_STATE,
  FINALIZED_REVIEW_STATUS,
  PRE_CLEAN_REVIEW_STATUS
} from "../constants";
import ImageLane from "./ImageLane";
import { useEffect } from "react";

const SubjectCleaningArea = ({ subject, sessionId, reviewStatus }) => {
  const refreshIfExpired = useRefreshSubjectIfExpired(subject);
  const [images, setImages] = useRecoilState(subjectImages(subject));
  const apiClient = useSessionApiClient();
  const refreshInterIdSection = useRefreshInterIdSection();
  const readOnly = (reviewStatus === FINALIZED_REVIEW_STATUS || reviewStatus === PRE_CLEAN_REVIEW_STATUS)

  // Please note that handleImageClick uses a default transition to know the new image state.
  // We can still support transitioning to any state as long as the semantics of the user interaction is
  // well-defined. As an example we could allow dragging an image from the CLEAN lane to the UNCLEAN one.
  // This interaction would be implemented in a different handle.
  const handleImageClick = async (image) => {
    if (!readOnly) {
      const nextState = image?.previousState
        ? image.previousState
        : DEFAULT_STATE_TRANSITIONS[image.imageState];
      let newImage = await apiClient.updateImageState(
        sessionId,
        image.id,
        nextState
      );
      newImage.previousState = image.imageState;
      setImages(
        images.map((item) => (item.id === newImage.id ? newImage : item))
      );
      // When navigating into the interId section it doesn't auto refresh.
      // Also, it should only display clean images. So it must be refreshed here
      // in order to update the displayed images
      refreshInterIdSection();
    }
  };

  const renderImageLane = ({ states, laneTitle }) => {
    if (!images) return;

    const filteredImages = images.filter(
      (image) => states.indexOf(image.imageState) !== -1
    );

    if (filteredImages.length === 0) return;

    return (
      <ImageLane
        images={filteredImages}
        onClick={handleImageClick}
        laneTitle={`${laneTitle} (${filteredImages.length} of ${images.length})`}
        totalImages={images.length}
        readOnly={readOnly}
      />
    );
  };

  useEffect(() => {
    refreshIfExpired();
  });

  return (
    <div>
      {renderImageLane({
        states: [CLEAN_IMAGE_STATE, MERGE_IMAGE_STATE],
        laneTitle: "Clean",
      })}
      {renderImageLane({ states: [UNCLEAN_IMAGE_STATE], laneTitle: "Unclean" })}
      {renderImageLane({
        states: [REMOVE_NOISY_IMAGE_STATE],
        laneTitle: "Noisy",
      })}
      {renderImageLane({
        states: [REMOVE_NEAR_DUPLICATE_IMAGE_STATE],
        laneTitle: "Near duplicates",
      })}
    </div>
  );
};

export default SubjectCleaningArea;
